import {
  IconButton,
  Stack,
  TableBody,
  TableProps,
  TableRow,
  Typography,
} from '@mui/material';
import React, { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Row,
  TableCellEmpty,
  TableCellHeader,
  TableContainerStyled,
  TableHeadStyled,
  TableStyled,
  TypographyHeader,
  Wrapper,
} from './jtable.styled';
import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { IconSize } from '@constants';
import TableColumn from '@models/table-column';
import { DroppableProvided } from 'react-beautiful-dnd';
import { DownloadFileLink } from '@atoms/DownloadFileLink';

interface IProps extends TableProps {
  tableTitle?: string;
  isNotFound?: boolean;
  columns: TableColumn[];
  notFoundLabel?: string;
  gridMenu?: ReactElement;
  perpage?: number;
  total?: number;
  children: ReactNode;
  nbpages?: number;
  onChangeOrder?: (field: string) => void;
  providedDrop?: DroppableProvided;
  withMinWidth?: boolean;
  showDownload?: boolean;
  linkDownload?: string;
}

export const JTable: React.FC<IProps> = ({
  tableTitle,
  children,
  columns,
  isNotFound,
  onChangeOrder,
  gridMenu,
  notFoundLabel,
  providedDrop,
  withMinWidth = true,
  showDownload,
  linkDownload,
  ...props
}): ReactElement => {
  const { t } = useTranslation();

  return (
    <Wrapper
      ref={providedDrop?.innerRef}
      {...providedDrop?.droppableProps}
      {...props}
    >
      <TableContainerStyled attrs={{ withMinWidth }}>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          {tableTitle && <Typography variant="h4">{tableTitle} </Typography>}

          {showDownload && linkDownload && !isNotFound && (
            <DownloadFileLink
              label={t('DOWNLOAD_CSV')}
              fileName={tableTitle ?? 'data_exported'}
              link={linkDownload}
            />
          )}
        </Stack>
        <TableStyled data-qa={`table-${tableTitle}`}>
          <TableHeadStyled>
            <TableRow>
              {columns.map((column) => {
                return (
                  <TableCellHeader key={column.name} align="center">
                    <Stack alignItems={'center'} direction={'row'} gap={2}>
                      <TypographyHeader> {t(column.name)} </TypographyHeader>
                      {column.orderable && column.sortingField && (
                        <IconButton
                          size="small"
                          onClick={() => onChangeOrder?.(column.sortingField)}
                          data-qa={`table-header-order-button-${column.name}`}
                        >
                          <ArrowsUpDownIcon height={IconSize.sm} />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCellHeader>
                );
              })}
            </TableRow>
          </TableHeadStyled>
          <TableBody>
            {isNotFound && notFoundLabel ? (
              <Row>
                <TableCellEmpty colSpan={columns.length + 1}>
                  <Typography>{t(notFoundLabel)}</Typography>
                </TableCellEmpty>
              </Row>
            ) : (
              children
            )}
          </TableBody>
        </TableStyled>
        {gridMenu}
      </TableContainerStyled>
    </Wrapper>
  );
};
